import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { AutoComplete } from 'primereact/autocomplete';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import LoadingScreen from 'src/components/LoadingScreen';
import { ModelOfVehicleItem } from 'src/database';

import { useListVehiclesController } from 'src/controllers/Vehicle/ListVehiclesController';
import { useFormCreateVehicle } from 'src/controllers/Vehicle/CreateVehiclesController';
import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import classNames from 'classnames';
import useIntersectionObserver from 'src/components/IntersectionObserver/useIntersectionObserver';
import { PulseLoader } from 'react-spinners';
import DropzoneScreen from 'src/components/Dropzone/Dropzone';
import { useData } from 'src/contexts/data';

interface filteredProps {
	label: string;
	value?: string;
}

const VehicleList: React.FC = () => {
	const [globalFilter, setGlobalFilter] = useState<string | null>(null);
	const [valueUnity, setValueUnity] = useState('');
	const [filteredUnity, setFilteredUnity] = useState<filteredProps[]>([{ label: '', value: '' }]);

	const { getListController, handleListController } = useListVehiclesController();
	const { getUnityListController } = useListUnitysController();
	const { getFormController } = useFormCreateVehicle();
	const { getImages } = useData();

	const categories = getFormController.tablesData.categoriesData.map(category => {
		return { label: category.DESCRICAO, value: category.ID };
	});
	const especie = getFormController.tablesData.especiesData.map(especie => {
		return { label: especie.DESCRICAO, value: especie.ID };
	});
	const status = [
		{ label: 'Em manutenção', value: 0 },
		{ label: 'Pronto para uso', value: 1 },
	];
	const brands = getFormController.tablesData.brandsData.map(brand => {
		return { label: brand.DESCRICAO, value: brand.ID };
	});
	const mileages = getFormController.tablesData.mileagesData.map(mileage => {
		return { label: mileage.DESCRICAO, value: mileage.ID };
	});
	const stock = getFormController.tablesData.stocksData.map(stock => {
		return { label: stock.NOME, value: stock.ID };
	});
	const fuelType = getFormController.tablesData.fuelTypesData.map(stock => {
		return { label: stock.DESCRICAO, value: stock.ID };
	});
	const unitys = getUnityListController.AllUnitysData.map(unity => {
		return { label: unity.NOME, value: unity.ID };
	});

	if (getListController.loading) {
		return <LoadingScreen />;
	}

	const searchUnitys = (event: any) => {
		let filteredUnitys;
		if (!event.query.trim().length) {
			filteredUnitys = [...unitys];
		} else {
			filteredUnitys = unitys.filter(unity => {
				return unity.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}
		setFilteredUnity(filteredUnitys);
	};

	const editVehicleDialogFooter = (
		<React.Fragment>
			<Button
				label='Cancelar'
				icon='pi pi-times'
				className='p-button-text'
				onClick={handleListController.closeEditDialog}
			/>
			<Button
				type='submit'
				label='Salvar'
				icon='pi pi-check'
				className='p-button-text'
				onClick={() => {
					getListController.formik.handleSubmit();
				}}
			/>
		</React.Fragment>
	);

	const deleteVehicleDialogFooter = (
		<React.Fragment>
			<Button
				label='Não'
				icon='pi pi-times'
				className='p-button-text'
				onClick={handleListController.closeDeleteDialog}
			/>
			<Button
				label='Sim'
				icon='pi pi-check'
				className='p-button-text'
				onClick={() => {
					getListController.selectedVehicle &&
						handleListController.removeVehicle(getListController.selectedVehicle);
				}}
			/>
		</React.Fragment>
	);

	const leftVehicleToolbar = () => {
		return (
			<Link to='/vehicleForm'>
				<Button label='Cadastrar veículo' icon='pi pi-plus' className='p-button-success p-mr-2' />
			</Link>
		);
	};

	const actionBodyTemplate = (vehicle: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<Button
					icon='pi pi-pencil'
					className='p-button-rounded p-button-text p-button-success '
					onClick={() => handleListController.openEditDialog(vehicle)}
				/>
				<Button
					icon='pi pi-trash'
					className='p-button-rounded p-button-text p-button-danger'
					onClick={() => handleListController.openDeleteDialog(vehicle)}
				/>
			</React.Fragment>
		);
	};

	const tableHeader = (
		<div className='table-header p-grid p-d-flex p-jc-between'>
			<h5 className='p-m-0'>Veículos</h5>
			<span className='p-input-icon-left p-search-bar'>
				<i className='pi pi-search' />
				<InputText
					type='search'
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalFilter(e.target.value)}
					placeholder='Buscar...'
				/>
			</span>
		</div>
	);

	const ID_VEICULO = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>ID</span>
				{rowData.ID}
			</React.Fragment>
		);
	};

	const HEADER_PLACA = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>PLACA</span>
				{rowData.PLACA}
			</React.Fragment>
		);
	};

	const HEADER_DESCRICAO = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>DESCRIÇÃO</span>
				{rowData.DESCRICAO}
			</React.Fragment>
		);
	};

	const HEADER_COR = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>COR</span>
				{rowData.COR}
			</React.Fragment>
		);
	};

	const HEADER_ANOMODELO = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>MODELO</span>
				{rowData.ANOMODELO}
			</React.Fragment>
		);
	};

	const HEADER_RENAVAM = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>RENAVAM</span>
				{rowData.RENAVAM}
			</React.Fragment>
		);
	};

	const HEADER_MARCA = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>MARCA</span>
				{rowData.DESCRICAO_MARCA}
			</React.Fragment>
		);
	};

	const HEADER_CATEGORIA = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>CATEGORIA</span>
				{rowData.DESCRICAO_CATEGORIA}
			</React.Fragment>
		);
	};

	const HEADER_TIPOCOMBUSTIVEL = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>COMBUSTIVEL</span>
				{rowData.TIPOCOMBUSTIVEL?.DESCRICAO}
			</React.Fragment>
		);
	};

	const HEADER_ANOLICENCIADO = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>LICENCIAMENTO</span>
				{rowData.ANOLICENCIADO}
			</React.Fragment>
		);
	};

	const HEADER_STATUS = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>STATUS</span>
				{
					status.find(statusItem => {
						return statusItem.value === rowData.STATUS;
					})?.label
				}
			</React.Fragment>
		);
	};

	const HEADER_ESPECIE = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>ESPECIE</span>
				{rowData.DESCRICAO_ESPECIE}
			</React.Fragment>
		);
	};

	const HEADER_ESTOQUE = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>ESTOQUE</span>
				{rowData.NOME_ESTOQUE}
			</React.Fragment>
		);
	};

	const HEADER_UNIDADE = (rowData: ModelOfVehicleItem) => {
		return (
			<React.Fragment>
				<span className='left-header'>UNIDADE</span>
				{rowData.NOME_ORGAOUNIDADE}
			</React.Fragment>
		);
	};

	const Loader: React.FC = ({ children }) => {
		const divRef: any = useRef<HTMLDivElement | null>(null);

		const [isVisible] = useIntersectionObserver({
			elementRef: divRef,
		});

		if (isVisible && !getListController.maxData && !getListController.loadingRows) {
			handleListController.setRows(getListController.rows + 20);
		}

		return (
			<div
				ref={divRef}
				style={{
					height: 'auto',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				{children}
			</div>
		);
	};

	const cellStyle = {
		fontSize: '10px' // Ajuste o tamanho da fonte para as células
	};

	return (
		<div className='p-card p-p-4 datatable-container' style={{ height: '80vh' }}>
			<Toast ref={getListController.toast} />
			<Toolbar className='p-mb-4' left={leftVehicleToolbar}></Toolbar>

			<DataTable
				dataKey='ID'
				value={getListController.allVehiclesData}
				header={tableHeader}
				rowHover
				globalFilter={globalFilter}
				autoLayout
				className='responsive-table p-datatable-sm'
				scrollable

				scrollHeight='100%'
			>
				{/*<Column field='ID' header='ID' body={ID_VEICULO} bodyStyle={cellStyle}></Column>*/}
				<Column field='PLACA' header='PLACA' body={HEADER_PLACA} bodyStyle={cellStyle}></Column>
				<Column field='DESCRICAO' header='DESCRIÇÃO' body={HEADER_DESCRICAO} bodyStyle={cellStyle}></Column>
				<Column field='COR' header='COR' body={HEADER_COR} bodyStyle={cellStyle}></Column>
				{/*<Column field='ANOMODELO' header='MODELO' body={HEADER_ANOMODELO} bodyStyle={cellStyle}></Column>
				<Column field='RENAVAM' header='RENAVAM' body={HEADER_RENAVAM} bodyStyle={cellStyle}></Column> */}
				<Column field='DESCRICAO_MARCA' header='MARCA' body={HEADER_MARCA} bodyStyle={cellStyle}></Column>
				{/*<Column field='DESCRICAO_CATEGORIA' header='CATEGORIA' body={HEADER_CATEGORIA} bodyStyle={cellStyle}></Column>
				<Column field='DESCRICAO_ESPECIE' header='ESPÉCIE' body={HEADER_ESPECIE} bodyStyle={cellStyle}></Column>*/}
				<Column field='FT_TIPOCOMBUSTIVEL' header='COMBUSTIVEL' body={HEADER_TIPOCOMBUSTIVEL} bodyStyle={cellStyle}></Column>
				{/*<Column field='ANOLICENCIADO' header='LICENCIAMENTO' body={HEADER_ANOLICENCIADO} bodyStyle={cellStyle}></Column>*/}
				<Column field='STATUS' header='STATUS' body={HEADER_STATUS} bodyStyle={cellStyle}></Column>
				<Column field='NOME_ESTOQUE' header='ESTOQUE' body={HEADER_ESTOQUE} bodyStyle={cellStyle}></Column>
				<Column field='NOME_ORGAOUNIDADE' header='SETOR' body={HEADER_UNIDADE} bodyStyle={cellStyle}></Column>
				<Column body={actionBodyTemplate} bodyStyle={{ textAlign: 'right', cellStyle }} ></Column>
			</DataTable>
			{/* <Loader>{!getListController.maxData ?
                getListController.loadingRows ? <PulseLoader color="#0388e5" size={8} /> : <></>
                : "Lista completa"}
            </Loader> */}

			<Dialog
				className='p-fluid'
				style={{ width: '90%' }}
				visible={getListController.editDialog}
				onHide={handleListController.closeEditDialog}
				onShow={() => {
					getListController.formik.setValues(getListController.selectedVehicle);
					setValueUnity(getListController.selectedVehicle.NOME_ORGAOUNIDADE || '');
					getImages(getListController.selectedVehicle.ID);
				}}
				footer={editVehicleDialogFooter}
				header={`Editar veículo ${getListController.selectedVehicle?.DESCRICAO} - ${getListController.selectedVehicle?.PLACA}`}
				modal>
				{getListController.loadingEdit ? <LoadingScreen color='#0388e5' /> : <></>}
				<div className='p-grid p-fluid'>
					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='descricao'>Descrição</label>
						<InputText
							id='descricao'
							maxLength={150}
							onChange={getListController.formik.handleChange('DESCRICAO')}
							value={getListController.formik.values.DESCRICAO}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.DESCRICAO &&
									getFormController.formik.errors.DESCRICAO,
								'popup-form-inactive': !(
									getFormController.formik.touched.DESCRICAO &&
									getFormController.formik.errors.DESCRICAO
								),
							})}>
							{getFormController.formik.errors.DESCRICAO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='sigla'>Sigla</label>
						<InputText
							id='sigla'
							maxLength={15}
							onChange={getListController.formik.handleChange('SIGLA')}
							value={getListController.formik.values.SIGLA}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.SIGLA && getFormController.formik.errors.SIGLA,
								'popup-form-inactive': !(
									getFormController.formik.touched.SIGLA && getFormController.formik.errors.SIGLA
								),
							})}>
							{getFormController.formik.errors.SIGLA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='estoque'>Estoque</label>
						<Dropdown
							id='estoque'
							value={getListController.formik.values.AX_ESTOQUE}
							onChange={e => getListController.formik.setFieldValue('AX_ESTOQUE', e.value)}
							options={stock}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.AX_ESTOQUE &&
									getFormController.formik.errors.AX_ESTOQUE,
								'popup-form-inactive': !(
									getFormController.formik.touched.AX_ESTOQUE &&
									getFormController.formik.errors.AX_ESTOQUE
								),
							})}>
							{getFormController.formik.errors.AX_ESTOQUE}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='status'>Status de veículo</label>
						<Dropdown
							id='status'
							value={getListController.formik.values.STATUS}
							onChange={e => getListController.formik.setFieldValue('STATUS', e.value)}
							options={status}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.STATUS && getFormController.formik.errors.STATUS,
								'popup-form-inactive': !(
									getFormController.formik.touched.STATUS && getFormController.formik.errors.STATUS
								),
							})}>
							{getFormController.formik.errors.STATUS}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='renavam'>Ranavam</label>
						<InputText
							id='renavam'
							maxLength={25}
							onChange={getListController.formik.handleChange('RENAVAM')}
							value={getListController.formik.values.RENAVAM}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.RENAVAM && getFormController.formik.errors.RENAVAM,
								'popup-form-inactive': !(
									getFormController.formik.touched.RENAVAM && getFormController.formik.errors.RENAVAM
								),
							})}>
							{getFormController.formik.errors.RENAVAM}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='placa'>Placa</label>
						<InputText
							id='placa'
							maxLength={15}
							onChange={getListController.formik.handleChange('PLACA')}
							value={getListController.formik.values.PLACA}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.PLACA && getFormController.formik.errors.PLACA,
								'popup-form-inactive': !(
									getFormController.formik.touched.PLACA && getFormController.formik.errors.PLACA
								),
							})}>
							{getFormController.formik.errors.PLACA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-6'>
						<label htmlFor='chassi'>Número Chassi</label>
						<InputText
							id='chassi'
							maxLength={25}
							onChange={getListController.formik.handleChange('NUMEROCHASSI')}
							value={getListController.formik.values.NUMEROCHASSI}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.NUMEROCHASSI &&
									getFormController.formik.errors.NUMEROCHASSI,
								'popup-form-inactive': !(
									getFormController.formik.touched.NUMEROCHASSI &&
									getFormController.formik.errors.NUMEROCHASSI
								),
							})}>
							{getFormController.formik.errors.NUMEROCHASSI}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-6'>
						<label htmlFor='marca'>Marca</label>
						<Dropdown
							id='marca'
							value={getListController.formik.values.FT_MARCA}
							onChange={e => {
								getListController.formik.setFieldValue('FT_MARCA', e.value);
							}}
							options={brands}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.FT_MARCA &&
									getFormController.formik.errors.FT_MARCA,
								'popup-form-inactive': !(
									getFormController.formik.touched.FT_MARCA &&
									getFormController.formik.errors.FT_MARCA
								),
							})}>
							{getFormController.formik.errors.FT_MARCA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='anoFab'>Ano de fabricação</label>
						<InputText
							id='anoFab'
							onChange={getListController.formik.handleChange('ANOFABRICACAO')}
							value={getListController.formik.values.ANOFABRICACAO}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.ANOFABRICACAO &&
									getFormController.formik.errors.ANOFABRICACAO,
								'popup-form-inactive': !(
									getFormController.formik.touched.ANOFABRICACAO &&
									getFormController.formik.errors.ANOFABRICACAO
								),
							})}>
							{getFormController.formik.errors.ANOFABRICACAO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='anoModelo'>Ano do modelo</label>
						<InputText
							id='anoModelo'
							onChange={getListController.formik.handleChange('ANOMODELO')}
							value={getListController.formik.values.ANOMODELO}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.ANOMODELO &&
									getFormController.formik.errors.ANOMODELO,
								'popup-form-inactive': !(
									getFormController.formik.touched.ANOMODELO &&
									getFormController.formik.errors.ANOMODELO
								),
							})}>
							{getFormController.formik.errors.ANOMODELO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='tipoCombustivel'>Tipo de combustível</label>
						<MultiSelect
							id='tipoCombustivel'
							value={getListController.formik.values.TIPOCOMBUSTIVEL?.ID}
							onChange={e => {
								getListController.formik.setFieldValue('TIPOCOMBUSTIVEL.ID', e.value);
							}}
							options={fuelType}
							optionLabel='label'
							className='multiselect-custom'
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.TIPOCOMBUSTIVEL &&
									getFormController.formik.errors.TIPOCOMBUSTIVEL,
								'popup-form-inactive': !(
									getFormController.formik.touched.TIPOCOMBUSTIVEL &&
									getFormController.formik.errors.TIPOCOMBUSTIVEL
								),
							})}>
							{getFormController.formik.errors.TIPOCOMBUSTIVEL}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='capCombustivel'>Capacidade de combustível</label>
						<InputText
							id='capCombustivel'
							onChange={getListController.formik.handleChange('CAPACIDADECOMBUSTIVEL')}
							value={getListController.formik.values.CAPACIDADECOMBUSTIVEL}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.CAPACIDADECOMBUSTIVEL &&
									getFormController.formik.errors.CAPACIDADECOMBUSTIVEL,
								'popup-form-inactive': !(
									getFormController.formik.touched.CAPACIDADECOMBUSTIVEL &&
									getFormController.formik.errors.CAPACIDADECOMBUSTIVEL
								),
							})}>
							{getFormController.formik.errors.CAPACIDADECOMBUSTIVEL}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-4'>
						<label htmlFor='tipokm'>Tipo Quilometragem</label>
						<Dropdown
							id='tipokm'
							value={getListController.formik.values.FT_TIPOQUILOMETRAGEM}
							onChange={e => getListController.formik.setFieldValue('FT_TIPOQUILOMETRAGEM', e.value)}
							options={mileages}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.FT_TIPOQUILOMETRAGEM &&
									getFormController.formik.errors.FT_TIPOQUILOMETRAGEM,
								'popup-form-inactive': !(
									getFormController.formik.touched.FT_TIPOQUILOMETRAGEM &&
									getFormController.formik.errors.FT_TIPOQUILOMETRAGEM
								),
							})}>
							{getFormController.formik.errors.FT_TIPOQUILOMETRAGEM}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='marcadorKm'>Quilometragem</label>
						<InputText
							id='marcadorKm'
							onChange={getListController.formik.handleChange('MARCADORKM')}
							value={getListController.formik.values.MARCADORKM}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.MARCADORKM &&
									getFormController.formik.errors.MARCADORKM,
								'popup-form-inactive': !(
									getFormController.formik.touched.MARCADORKM &&
									getFormController.formik.errors.MARCADORKM
								),
							})}>
							{getFormController.formik.errors.MARCADORKM}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='potCilindrada'>Potência/ Cilindrada</label>
						<InputText
							id='potCilindrada'
							maxLength={20}
							onChange={getListController.formik.handleChange('CAPCIDADEPOTENCIACILINDRADA')}
							value={getListController.formik.values.CAPCIDADEPOTENCIACILINDRADA}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.CAPCIDADEPOTENCIACILINDRADA &&
									getFormController.formik.errors.CAPCIDADEPOTENCIACILINDRADA,
								'popup-form-inactive': !(
									getFormController.formik.touched.CAPCIDADEPOTENCIACILINDRADA &&
									getFormController.formik.errors.CAPCIDADEPOTENCIACILINDRADA
								),
							})}>
							{getFormController.formik.errors.CAPCIDADEPOTENCIACILINDRADA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='categoria'>Categoria</label>
						<Dropdown
							id='categoria'
							value={getListController.formik.values.FT_CATEGORIA}
							onChange={e => getListController.formik.setFieldValue('FT_CATEGORIA', e.value)}
							options={categories}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.FT_CATEGORIA &&
									getFormController.formik.errors.FT_CATEGORIA,
								'popup-form-inactive': !(
									getFormController.formik.touched.FT_CATEGORIA &&
									getFormController.formik.errors.FT_CATEGORIA
								),
							})}>
							{getFormController.formik.errors.FT_CATEGORIA}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='especie'>Espécie de veículo</label>
						<Dropdown
							id='especie'
							value={getListController.formik.values.FT_ESPECIE}
							onChange={e => getListController.formik.setFieldValue('FT_ESPECIE', e.value)}
							options={especie}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.FT_ESPECIE &&
									getFormController.formik.errors.FT_ESPECIE,
								'popup-form-inactive': !(
									getFormController.formik.touched.FT_ESPECIE &&
									getFormController.formik.errors.FT_ESPECIE
								),
							})}>
							{getFormController.formik.errors.FT_ESPECIE}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='corVeiculo'>Cor</label>
						<InputText
							id='corVeiculo'
							maxLength={10}
							onChange={getListController.formik.handleChange('COR')}
							value={getListController.formik.values.COR}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.COR && getFormController.formik.errors.COR,
								'popup-form-inactive': !(
									getFormController.formik.touched.COR && getFormController.formik.errors.COR
								),
							})}>
							{getFormController.formik.errors.COR}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='ctfReg'>Certificado Registro</label>
						<InputText
							id='ctfReg'
							maxLength={50}
							onChange={getListController.formik.handleChange('CERTIFICADOREGISTRO')}
							value={getListController.formik.values.CERTIFICADOREGISTRO}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.CERTIFICADOREGISTRO &&
									getFormController.formik.errors.CERTIFICADOREGISTRO,
								'popup-form-inactive': !(
									getFormController.formik.touched.CERTIFICADOREGISTRO &&
									getFormController.formik.errors.CERTIFICADOREGISTRO
								),
							})}>
							{getFormController.formik.errors.CERTIFICADOREGISTRO}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-3'>
						<label htmlFor='numMotor'>Número motor</label>
						<InputText
							id='numMotor'
							maxLength={25}
							onChange={getListController.formik.handleChange('NUMEROMOTOR')}
							value={getListController.formik.values.NUMEROMOTOR}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.NUMEROMOTOR &&
									getFormController.formik.errors.NUMEROMOTOR,
								'popup-form-inactive': !(
									getFormController.formik.touched.NUMEROMOTOR &&
									getFormController.formik.errors.NUMEROMOTOR
								),
							})}>
							{getFormController.formik.errors.NUMEROMOTOR}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-4'>
						<label htmlFor='telefone'>Unidade</label>
						<AutoComplete
							id='unidade'
							value={valueUnity}
							field='label'
							placeholder='Buscar...'
							onChange={e => {
								setValueUnity(e.value);
								getListController.formik.setFieldValue(
									'ORGAOUNIDADE',
									filteredUnity.find(unity => {
										return e.value === unity.label;
									})?.value || '',
								);
							}}
							onSelect={e => {
								setValueUnity(e.value.label);
								getListController.formik.setFieldValue('ORGAOUNIDADE', e.value.value);
							}}
							suggestions={filteredUnity}
							completeMethod={searchUnitys}
							dropdown
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.ORGAOUNIDADE &&
									getFormController.formik.errors.ORGAOUNIDADE,
								'popup-form-inactive': !(
									getFormController.formik.touched.ORGAOUNIDADE &&
									getFormController.formik.errors.ORGAOUNIDADE
								),
							})}>
							{getFormController.formik.errors.ORGAOUNIDADE}
						</div>
					</div>

					<div className='p-field p-col-12 p-md-2'>
						<label htmlFor='anoLicens'>Ano licenciamento</label>
						<InputText
							id='anoLicens'
							onChange={getListController.formik.handleChange('ANOLICENCIADO')}
							value={getListController.formik.values.ANOLICENCIADO}
						/>
						<div
							className={classNames('p-invalid popup-fade', {
								'popup-form-active':
									getFormController.formik.touched.ANOLICENCIADO &&
									getFormController.formik.errors.ANOLICENCIADO,
								'popup-form-inactive': !(
									getFormController.formik.touched.ANOLICENCIADO &&
									getFormController.formik.errors.ANOLICENCIADO
								),
							})}>
							{getFormController.formik.errors.ANOLICENCIADO}
						</div>
					</div>

					<div className='p-field p-col-12'>
						<label htmlFor='ultVenda'>Fotos de veículo</label>
						<div className='p-fileupload'>
							<DropzoneScreen />
						</div>
					</div>
				</div>
			</Dialog>

			<Dialog
				style={{ width: '450px' }}
				visible={getListController.deleteDialog}
				onHide={handleListController.closeDeleteDialog}
				footer={deleteVehicleDialogFooter}
				header='Remover veículo'
				modal>
				<div className='confirmation-content'>
					<i className='pi pi-exclamation-triangle p-mr-3' style={{ fontSize: '2rem' }} />
					{getListController.selectedVehicle && (
						<span>
							Você tem certeza que deseja deletar o veiculo{' '}
							<b>
								{getListController.selectedVehicle.DESCRICAO} -{' '}
								{getListController.selectedVehicle?.PLACA}
							</b>
							?
						</span>
					)}
				</div>
			</Dialog>
		</div>
	);
};

export default VehicleList;
